import { useCallback, useState } from "react";

import { detectProviderFromRawPaths } from "../../provider-config";
import {
  VENDOR_AGNOSTIC_PROVIDER,
  type SkillCollectionProviderSelection,
} from "../../skill-presets";
import type { SkillCollectionEditorStore } from "../../store";
import {
  canPickSkillFolderImport,
  pickSkillFolderImport,
  readSkillDropImport,
  readSkillFolderImport,
  type SkillFolderImportResult,
} from "../../utils/skill-folder-import";

import { ToastType } from "@/enums";
import { useOrganizationSubscriptionData } from "@/providers";
import { displayErrorToast, displayToast } from "@/utils/toast";

interface UseFolderImportParams {
  store: SkillCollectionEditorStore;
  provider: SkillCollectionProviderSelection;
  onProviderChange?: (preset: SkillCollectionProviderSelection) => void;
  setExpandedFolders: React.Dispatch<React.SetStateAction<Set<string>>>;
}

export const useFolderImport = ({
  store,
  provider,
  onProviderChange,
  setExpandedFolders,
}: UseFolderImportParams) => {
  const { subscription } = useOrganizationSubscriptionData();
  const [importProgress, setImportProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const isImportingFolder = importProgress !== null;

  const handleFolderImport = useCallback(
    async (files?: FileList, items?: DataTransferItemList) => {
      if (!files && !canPickSkillFolderImport()) {
        return false;
      }

      setImportProgress({ current: 0, total: 0 });
      try {
        let result: SkillFolderImportResult;
        if (items) {
          result = await readSkillDropImport(items, files);
        } else if (files) {
          result = await readSkillFolderImport(files);
        } else {
          result = await pickSkillFolderImport();
        }

        const { files: entries, rawPaths, skippedOversizedCount, skippedUnsupportedCount } = result;
        const fileLimit = subscription?.max_skill_files_per_collection ?? null;
        const currentFileCount = store.filePaths.size;
        let remainingNewFileSlots =
          fileLimit === null ? Number.POSITIVE_INFINITY : Math.max(0, fileLimit - currentFileCount);
        const allowedEntries = entries.filter(entry => {
          if (store.filePaths.has(entry.path)) {
            return true;
          }

          if (remainingNewFileSlots <= 0) {
            return false;
          }

          remainingNewFileSlots -= 1;
          return true;
        });
        const skippedForPlanLimitCount = entries.length - allowedEntries.length;

        if (allowedEntries.length === 0) {
          let emptyImportMessage = "No importable files were found in that folder.";
          if (skippedUnsupportedCount > 0) {
            emptyImportMessage =
              "No importable files were found. Office documents, archives, and executables are not supported.";
          } else if (skippedOversizedCount > 0) {
            emptyImportMessage =
              "No importable files were found. Files larger than 5 MB are not supported.";
          } else if (skippedForPlanLimitCount > 0 && fileLimit !== null) {
            emptyImportMessage = `This import would exceed your plan's limit of ${fileLimit} files per skill collection.`;
          }

          displayErrorToast(emptyImportMessage);
          return true;
        }

        setImportProgress({ current: 0, total: allowedEntries.length });

        await store.importLocalFilesAsync(allowedEntries, (current, total) => {
          setImportProgress({ current, total });
        });

        if (provider === VENDOR_AGNOSTIC_PROVIDER && onProviderChange) {
          const detected = detectProviderFromRawPaths(rawPaths);
          if (detected) {
            onProviderChange(detected);
          }
        }

        const paths = allowedEntries.map(entry => entry.path);
        setExpandedFolders(previous => {
          const next = new Set(previous);
          paths.forEach(path => {
            const parts = path.split("/").filter(Boolean);
            for (let i = 0; i < parts.length - 1; i += 1) {
              next.add(parts.slice(0, i + 1).join("/"));
            }
          });
          return next;
        });

        displayToast(`Imported ${allowedEntries.length} file(s)`, ToastType.success);

        if (skippedUnsupportedCount > 0) {
          displayToast(
            `${skippedUnsupportedCount} file(s) were skipped — office documents, archives, and executables are not supported.`,
            ToastType.warning,
          );
        }

        if (skippedOversizedCount > 0) {
          displayToast(
            `${skippedOversizedCount} file(s) larger than 5 MB were skipped.`,
            ToastType.warning,
          );
        }

        if (skippedForPlanLimitCount > 0 && fileLimit !== null) {
          displayToast(
            `${skippedForPlanLimitCount} file(s) were skipped to stay within your plan's ${fileLimit}-file limit for this skill collection.`,
            ToastType.warning,
          );
        }

        if (allowedEntries.some(entry => entry.path.startsWith("__flat/"))) {
          displayToast(
            "Some dropped files had no folder path from the browser, so they were placed under __flat/. If that looks wrong, use the import button or drag from Chrome/Safari.",
            ToastType.warning,
          );
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return true;
        }

        displayErrorToast("Could not read the selected folder.");
      } finally {
        setImportProgress(null);
      }

      return true;
    },
    [
      store,
      provider,
      onProviderChange,
      setExpandedFolders,
      subscription?.max_skill_files_per_collection,
    ],
  );

  return { importProgress, isImportingFolder, handleFolderImport };
};
